import type { TOC } from '@ember/component/template-only';
import { fn } from '@ember/helper';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import { didInsert, didUpdate } from '@ember/render-modifiers';
import {
  PrismBanner,
  PrismButton,
  PrismLoadingBar,
  PrismViewSwitcher,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { and } from 'ember-truth-helpers';
import GeneralButtonsLiveshopButton from 'lumen/components/general/buttons/liveshop-button';
import GeneralCalendarsCalendarBase from 'lumen/components/general/calendars/calendar-base';
import GeneralDatepickersDatepickerRange from 'lumen/components/general/datepickers/datepicker-range';
import GeneralDatepickersDatepickerWithSidebar from 'lumen/components/general/datepickers/datepicker-with-sidebar';
import GeneralEmptyStateOtbBanner from 'lumen/components/general/empty-state-otb-banner';
import GeneralPageHeader from 'lumen/components/general/page/header';
import GeneralPageMain from 'lumen/components/general/page/main';
import TrophyModal from 'lumen/components/general/roi-trophies/trophy-modal';
import HotelPricingAssistantAutopilotBanner from 'lumen/components/hotel/pricing-assistant/autopilot-banner';
import HotelPricingAssistantCalendarsDaysDayPricingAssistant from 'lumen/components/hotel/pricing-assistant/calendars/days/day-pricing-assistant';
import HotelPricingAssistantDataSyncStatus from 'lumen/components/hotel/pricing-assistant/data-sync-status';
import HotelPricingAssistantFiltersPricingSelections from 'lumen/components/hotel/pricing-assistant/filters/pricing-selections';
import HotelPricingAssistantFiltersPricingSelectionsDynamicRate from 'lumen/components/hotel/pricing-assistant/filters/pricing-selections-dynamic-rate';
import HotelPricingAssistantPageModeViewSwitcher from 'lumen/components/hotel/pricing-assistant/page-mode-view-switcher';
import HotelPricingAssistantPmsSsEntryPointBanner from 'lumen/components/hotel/pricing-assistant/pms-ss-entry-point-banner';
import DayOverrideModal from 'lumen/components/hotel/pricing-assistant/table/modals/day-override-modal/day-override-modal';
import HotelPricingAssistantTableTablePricingManager from 'lumen/components/hotel/pricing-assistant/table/table-pricing-manager';
import HotelRatesModalsCalendarSettingsModal from 'lumen/components/hotel/rates/modals/calendar-settings-modal';
import HotelAndMarketDropdownsAccountSelectorWrapper from 'lumen/components/hotel-and-market/dropdowns/account-selector-wrapper';
import type HotelPricingManagerController from 'lumen/controllers/hotel/pricing-manager';
import {
  OTB_SS_ENTRY_POINTS,
  OTB_SS_VIA_QP_VALUES,
} from 'lumen/utils/constants/otb-selfservice-constants';
import { PRIMA_TABLE_LAYOUTS } from 'lumen/utils/constants/pricing-assistant-constants';

interface HotelPricingManagerSignature {
  Args: {
    controller: HotelPricingManagerController;
    model: unknown;
  };
}

<template>
  <GeneralPageHeader>
    <div class="content-wrapper">
      <div class="flex flex-row justify-between">
        <div class="flex gap-400">
          <HotelAndMarketDropdownsAccountSelectorWrapper @hasDivider={{true}} />
          <div class="ic-product-tour-datepicker-pricing-manager">
            {{#if @controller.isStayDateRangePickerShown}}
              {{! Snapping calendar clicks to whole months leaves no free-form range to pick }}
              {{! @glint-expect-error: Incorrect type }}
              <GeneralDatepickersDatepickerWithSidebar
                @displayEvents={{true}}
                @maxDate={{@controller.maxDateMoment}}
                @minDate={{@controller.stayDateRangeMinDate}}
                @range={{@controller.stayDateRange}}
                @rangeChanged={{@controller.updateStayDateRange}}
                @selectStandardRange={{true}}
                @sidebarLists={{@controller.stayDateRangeOptions}}
                @standardRange="month"
              />
            {{else}}
              <GeneralDatepickersDatepickerRange
                @displayEvents={{true}}
                @maxDate={{@controller.maxDateMoment}}
                @minDate={{@controller.minDateMoment}}
                {{! @glint-expect-error: Incorrect type }}
                @range={{@controller.dateRange}}
                @rangeChanged={{@controller.updateSelectedRange}}
                @selectStandardRange={{true}}
                @standardRange={{@controller.datepickerStandardRange}}
              />
            {{/if}}
          </div>
        </div>
        {{#unless @controller.access.isPricingAssistantStandaloneVersion}}
          <div
            class="pricing-assistant-page-mode-switcher ic-product-tour-60133597-view-switcher flex flex-row items-center justify-center"
          >
            <HotelPricingAssistantPageModeViewSwitcher
              @pageMode={{@controller.pageMode}}
              @updatePageMode={{@controller.updatePageMode}}
            />
          </div>
        {{/unless}}

        <div class="flex flex-row items-center gap-200">
          {{! @glint-expect-error: Incorrect type }}
          {{#if (and @controller.view @controller.isViewSwitcherEnabled)}}
            <PrismViewSwitcher
              {{! @glint-expect-error: Incorrect type }}
              @activeButtonId={{@controller.view}}
              @onSetActiveButtonId={{@controller.switchTab}}
              as |ViewSwitcher|
            >
              <ViewSwitcher.button
                @icon="view-calendar"
                @id="calendar"
                @label=""
              />
              <ViewSwitcher.button @icon="view-table" @id="table" @label="" />
            </PrismViewSwitcher>
          {{/if}}

          {{#if @controller.isStayDateRangePickerShown}}
            <PrismViewSwitcher
              @activeButtonId={{@controller.tableLayout}}
              @onSetActiveButtonId={{@controller.switchTableLayout}}
              as |ViewSwitcher|
            >
              <ViewSwitcher.button
                @id={{PRIMA_TABLE_LAYOUTS.MONTH}}
                @label={{t "general.month"}}
              />
              <ViewSwitcher.button
                @id={{PRIMA_TABLE_LAYOUTS.WEEK}}
                @label={{t "general.week"}}
              />
            </PrismViewSwitcher>
          {{/if}}

          {{#unless @controller.access.isPricingAssistantStandaloneVersion}}
            <GeneralButtonsLiveshopButton
              @hasLiveShop={{@controller.liveupdateEnabled}}
              @onClick={{@controller.startUpdate}}
              @selectedOTA={{@controller.selectedOTA}}
            />
          {{/unless}}
        </div>
      </div>
    </div>
  </GeneralPageHeader>

  <GeneralPageHeader @isSmall={{true}}>
    <div
      class="content-wrapper flex items-center justify-between pt-200 pb-200"
    >
      {{! For now we only make filter changes on DRP table view }}
      {{#if
        (and
          @controller.access.hasPricingAssistantDynamicRatePricingFeature
          @controller.isTableView
        )
      }}
        <HotelPricingAssistantFiltersPricingSelectionsDynamicRate
          @los={{@controller.los}}
          @maxPersons={{@controller.maxPersons}}
          @mealPlan={{@controller.mealPlan}}
          @platform={{@controller.platform}}
          @rateCode={{@controller.rateCode}}
          @rateParam={{@controller.rateParam}}
          @selectedOTA={{@controller.selectedOTA}}
          @updateMealPlan={{@controller.updateMealPlan}}
          @updateRateCode={{@controller.updateRateCode}}
          @updateRateParam={{@controller.updateRateParam}}
        />
      {{else}}
        <HotelPricingAssistantFiltersPricingSelections
          @isPeriodsRequestLoading={{@controller.isPeriodsRequestLoading}}
          @isTableView={{@controller.isTableView}}
          @los={{@controller.los}}
          @maxPersons={{@controller.maxPersons}}
          @mealPlan={{@controller.mealPlan}}
          @rateParam={{@controller.rateParam}}
          @ratePreferenceSettingsInProgress={{@controller.ratePreferenceSettingsInProgress}}
          @roomType={{@controller.roomType}}
          @selectedOTA={{@controller.selectedOTA}}
          @updateGuests={{@controller.updateGuests}}
          @updateRoomType={{@controller.updateRoomType}}
        />
      {{/if}}
      <div class="flex flex-row items-center gap-200 text-nowrap">
        {{#if @controller.hasBulkUpdateRights}}
          <PrismButton
            @buttonType="secondary"
            @iconBefore="edit-pencil"
            @label={{t "general.bulk_update"}}
            @onClick={{@controller.showBulkUpdateModal}}
          />
        {{/if}}
        {{#if @controller.isCalendarView}}
          <PrismButton
            @buttonType="secondary"
            @iconBefore="sliders"
            @label={{t "rates.calendar_settings"}}
            @onClick={{fn @controller.showCalendarSettingsModal true}}
          />
        {{/if}}

        {{#if @controller.arePricingPageModeRequestsLoading}}
          <div class="update-timestamp">
            <div
              class="update-timestamp-loading-placeholder loading-pulse"
            ></div>
          </div>
        {{else}}
          <HotelPricingAssistantDataSyncStatus
            @arePageRequestsLoading={{@controller.arePricingPageModeRequestsLoading}}
            @currentSelectedMonth={{@controller.monthMoment}}
            @hasLiveShop={{@controller.liveupdateEnabled}}
            @periods={{@controller.monthOnlyModel}}
            @requestPricingAssistantPmsInfoTaskResult={{@controller.model.requestPricingAssistantPmsInfoTaskResult}}
            @requestPricingAssistantSubscriptionMetaTaskResult={{@controller.model.requestPricingAssistantSubscriptionMetaTaskResult}}
          />
        {{/if}}
      </div>
    </div>
  </GeneralPageHeader>

  <GeneralPageMain
    {{didUpdate
      @controller.onPricingAssistantRequestsLoadingUpdate
      @controller.arePricingPageModeRequestsLoading
    }}
  >

    {{#if @controller.arePricingPageModeRequestsLoading}}
      <PrismLoadingBar
        @isFixed={{true}}
        @positionTop={{@controller.mainLoadingBarPositionTop}}
      />
    {{/if}}

    {{#if @controller.isCalendarView}}
      <div class="content-wrapper main-content--new-layout">
        {{! Banner height can change on container resizing or when user dismisses the banner. We want the effective container height to be updated in those cases }}
        <div
          {{didInsert @controller.setBannerHeight}}
          {{didUpdate
            @controller.setBannerHeight
            @controller.containerHeight
            @controller.containerWidth
            @controller.pricingAssistantPmsSs.isPricingAssistantPMSSelfServicePrimaryBannerVisible
            @controller.pricingAssistantPmsSs.isPricingAssistantOtbSelfServiceUpsellPrimaryBannerVisible
            @controller.pricingAssistantPmsSs.isPricingAssistantPMSSelfServiceInProgress
            @controller.showAutopilotBanner
          }}
        >
          {{#if
            @controller.pricingAssistantPmsSs.isPricingAssistantPMSSelfServicePrimaryBannerVisible
          }}
            <div class="mb-400">
              <HotelPricingAssistantPmsSsEntryPointBanner
                @bannerMessage={{@controller.pmsSsEntryPointBannerMessage}}
              />
            </div>
          {{else if
            @controller.pricingAssistantPmsSs.isPricingAssistantPMSSelfServiceInProgress
          }}
            <div class="mb-400">
              <PrismBanner
                @bannerMessage={{t
                  "pricing_assistant.self_service.banner.pms_connection_in_progress_message"
                }}
              />
            </div>
          {{else if
            @controller.pricingAssistantPmsSs.isPricingAssistantOtbSelfServiceUpsellPrimaryBannerVisible
          }}
            <div class="mb-400">
              <GeneralEmptyStateOtbBanner
                @otbSelfServiceEntryPoint={{OTB_SS_ENTRY_POINTS.PRICING_ASSISTANT_CALENDAR}}
                @otbSelfServiceEntryProduct={{OTB_SS_VIA_QP_VALUES.PRICING_ASSISTANT}}
              />
            </div>
          {{else if @controller.showAutopilotBanner}}
            <div class="mb-400">
              <HotelPricingAssistantAutopilotBanner
                @onAutopilotBannerClose={{@controller.onAutopilotBannerClose}}
              />
            </div>
          {{/if}}
        </div>
        <div
          class="card-tile card-tile--overflow-hidden ic-product-tour-calendar-pricing-manager"
        >
          {{! @glint-expect-error: Incorrect type }}
          <GeneralCalendarsCalendarBase
            @calenderHeaderClass="ins-calendar-header--new-layout"
            @containerHeight={{@controller.containerHeight}}
            @dayComponent={{HotelPricingAssistantCalendarsDaysDayPricingAssistant}}
            @isLoading={{@controller.isCalendarPricingManagerDataLoading}}
            @minDayComponentHeight={{95}}
            @model={{@controller.periods}}
            @reservedHeight={{@controller.calendarReservedHeight}}
          >
            <:dayComponent as |Component|>
              {{! @glint-expect-error: Incorrect type }}
              <Component.day
                @competitorsForCurrentHotelAndOTA={{@controller.competitorsForCurrentHotelAndOTA}}
                @currentSelectedMonth={{@controller.monthMoment}}
                @los={{@controller.los}}
                @maxPersons={{@controller.maxPersons}}
                @redirectToDayDetail={{@controller.redirectToDayDetail}}
                @selectedRoomCategory={{@controller.selectedRoomCategory}}
              />
            </:dayComponent>
          </GeneralCalendarsCalendarBase>
        </div>
      </div>
    {{else if @controller.isTableView}}
      <div
        class="content-wrapper main-content pricing-manager-with-popover py-400"
      >
        {{#unless @controller.isMonthTableLayout}}
          <HotelPricingAssistantTableTablePricingManager
            @competitorsForCurrentHotelAndOTA={{@controller.competitorsForCurrentHotelAndOTA}}
            @flattenedRoomOffsetHierarchy={{@controller.flattenedRoomOffsetHierarchy}}
            @isHeaderLoading={{@controller.isDayBarDataLoading}}
            @isLoading={{@controller.isTablePricingManagerDataLoading}}
            @model={{@controller.weekOnlyModel}}
            @onDayHeaderClick={{@controller.openDayOverrideModal}}
            @range={{@controller.dateRange}}
            @rateCode={{@controller.rateCode}}
            @refDaysAgo={{@controller.refDaysAgo}}
          />
        {{/unless}}
      </div>
    {{/if}}
  </GeneralPageMain>

  {{#if @controller.dayOverrideModalPeriod}}
    <DayOverrideModal
      @onCloseModal={{@controller.closeDayOverrideModal}}
      @period={{@controller.dayOverrideModalPeriod}}
    />
  {{/if}}

  {{#if
    (and @controller.isCalendarView @controller.isShowingCalendarSettingsModal)
  }}
    <HotelRatesModalsCalendarSettingsModal
      @closeModal={{fn @controller.showCalendarSettingsModal false}}
      {{! @glint-expect-error: Incorrect type }}
      @isPricingAssistantProduct={{true}}
    />
  {{/if}}

  {{#if
    (and
      @controller.access.hasTrophyHighlights
      @controller.roiTrophies.pricePushMilestone
    )
  }}
    <TrophyModal
      @activeTrophy={{@controller.roiTrophies.pricePushTrophy}}
      @onDismiss={{@controller.roiTrophies.dismissPricePushMilestone}}
    />
  {{/if}}

  {{outlet}}
</template> satisfies TOC<HotelPricingManagerSignature>;
